import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Truck, ShieldCheck, RotateCcw, Send, Building2 } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';
import Logo from '../Logo';

const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center px-1.5" title="Visa">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Visa"><text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center" title="Mastercard">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Mastercard"><circle cx="19" cy="16" r="9" fill="#EB001B" /><circle cx="29" cy="16" r="9" fill="#F79E1B" /><path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" /></svg>
    </div>
    <div className="h-7 w-12 bg-[#2E77BB] rounded-md flex items-center justify-center px-1" title="American Express">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="American Express"><rect x="0" y="0" width="48" height="32" fill="#2E77BB" /><text x="24" y="14" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">AMERICAN</text><text x="24" y="22" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">EXPRESS</text><text x="24" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="1.2">AMEX</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center px-1" title="Discover">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Discover"><text x="22" y="21" textAnchor="middle" fontSize="8" fontWeight="800" fill="#231F20" fontFamily="Arial, sans-serif">DISC</text><circle cx="40" cy="18" r="4" fill="#F76B1C" /></svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const trust = [
    { icon: Truck, title: 'Free USA Shipping', text: 'USPS on every order' },
    { icon: RotateCcw, title: '30-Day Returns', text: 'Simple, no-hassle' },
    { icon: ShieldCheck, title: 'Secure Checkout', text: 'Encrypted payments' },
  ];
  const support = [
    { name: 'Shipping', path: '/shipping' },
    { name: 'Returns', path: '/returns' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Our Mission', path: '/our-mission' },
    { name: 'Why Choose Us', path: '/why-choose-us' },
    { name: 'Quality Guarantee', path: '/quality-guarantee' },
  ];

  return (
    <footer className="mt-20">
      {/* Trust strip */}
      <div className="bg-white border-t border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {trust.map((t) => (
            <div key={t.title} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-athletic-50 text-athletic-700 flex items-center justify-center flex-shrink-0"><t.icon className="w-5 h-5" /></div>
              <div><p className="text-dark-900 font-semibold text-sm">{t.title}</p><p className="text-dark-500 text-xs">{t.text}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Main footer (light warm) */}
      <div className="bg-dark-100 border-t border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="col-span-2 lg:col-span-4">
            <Logo />
            <p className="text-dark-600 text-sm leading-relaxed my-5 max-w-sm">Thoughtfully chosen hardware and power tools for professionals and weekend makers &mdash; measuring gear, hand tools, cordless power tools and storage, shipped free across the USA.</p>
            <div className="space-y-2 text-sm">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2 text-dark-700 hover:text-athletic-700 transition-colors"><Phone className="w-4 h-4 mt-0.5 text-athletic-600" /> <span>{CONTACT_INFO.phone}</span></a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2 text-dark-700 hover:text-athletic-700 transition-colors"><Mail className="w-4 h-4 mt-0.5 text-athletic-600" /> <span className="break-all">{CONTACT_INFO.email}</span></a>
              <div className="flex items-start gap-2 text-dark-700"><MapPin className="w-4 h-4 mt-0.5 text-athletic-600 flex-shrink-0" /><span>{CONTACT_INFO.address.street}<br />{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</span></div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-dark-900 font-semibold text-sm mb-4">Shop</h4>
            <ul className="space-y-2.5">
              <li><Link to="/shop" className="text-dark-600 hover:text-athletic-700 text-sm transition-colors">All Tools</Link></li>
              {categories.map((cat) => (<li key={cat.id}><Link to={`/shop?category=${cat.slug}`} className="text-dark-600 hover:text-athletic-700 text-sm transition-colors">{cat.name}</Link></li>))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-dark-900 font-semibold text-sm mb-4">Support</h4>
            <ul className="space-y-2.5">
              {support.map((link) => (<li key={link.path}><Link to={link.path} className="text-dark-600 hover:text-athletic-700 text-sm transition-colors">{link.name}</Link></li>))}
              <li><Link to="/about" className="text-dark-600 hover:text-athletic-700 text-sm transition-colors">Our Story</Link></li>
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-4">
            <div className="bg-white rounded-2xl border border-dark-200 p-6 shadow-card">
              <h4 className="text-dark-900 font-semibold text-base mb-1">Join the workshop list</h4>
              <p className="text-dark-500 text-sm mb-4">New arrivals, restock alerts and honest tool tips &mdash; no spam.</p>
              <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
                <input type="email" placeholder="Your email address" className="flex-1 px-4 py-3 rounded-xl text-dark-900 text-sm bg-dark-50 border border-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-athletic-500" />
                <button type="submit" className="px-4 py-3 rounded-xl bg-athletic-600 text-white font-semibold text-sm flex items-center gap-2 hover:bg-athletic-700 transition-colors"><Send className="w-4 h-4" /></button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-dark-900 text-dark-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-dark-400">
            <span>&copy; {currentYear} Ironworks Tools</span>
            <span className="hidden sm:block text-white/15">|</span>
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-athletic-400" /> {CONTACT_INFO.corpName}</span>
            <span className="hidden sm:block text-white/15">|</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <div className="flex items-center gap-3"><span className="text-dark-500 text-xs font-medium">We Accept</span><PaymentLogos /></div>
          <p className="text-xs text-dark-500">Charges show as &quot;<span className="font-semibold text-dark-200">{CARD_DESCRIPTOR}</span>&quot;</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
